import { Context } from "../../../core/impl/Context";
import { CountriesManager } from "../../../countrydetection/impl/manager/CountriesManager";
import { Country } from "../../../countrydetection/impl/model/Country";
import { NetworkSettings } from "../../../config/NetworkSettings";
import { NetworkProperties } from "../model/NetworkProperties";
import { NetworkInstance } from "../model/NetworkInstance";
import { getLogger } from "@goodgamestudios/logger";

const logger = getLogger("WorldAssignment.NetworkXMLParser");

function readNumberChildTagValue(parent: Element, childTagName: string): number {
    const item = parent.querySelector(childTagName);

    return item ? +item.textContent : 0;
}

function readBooleanChildTagValue(parent: Element, childTagName: string): boolean {
    const item = parent.querySelector(childTagName);
    return item ? item.textContent === "true" : false;
}

function readStringChildTagValue(parent: Element, childTagName: string): string {
    const item = parent.querySelector(childTagName);
    return item ? item.textContent : "";
}

export const parseInstances = (data: any): Array<NetworkInstance> => parseInstancesList(data, "instances");
export const parseTestInstances = (data: any): Array<NetworkInstance> => parseInstancesList(data, "test-instances");

const parseInstancesList = (data: any, key: string): Array<NetworkInstance> => {
    let parser = new DOMParser();
    let doc: Document = parser.parseFromString(data, "text/xml");
    let instancesElement: Element = doc.querySelector(key);
    let instances: NetworkInstance[] = [];
    let instance: NetworkInstance;

    if (!instancesElement) {
        logger.debug("node '" + key + "' not found");
        return [];
    }

    if (instancesElement.children && instancesElement.children.length) {
        for (let i = 0; i < instancesElement.children.length; i++) {
            instance = parseNetworkInstance(instancesElement.children.item(i));
            instances.push(instance);
        }
    } else {
        logger.debug("node '" + key + "' is null or empty");
    }

    return instances;
}

const parseNetworkInstance = (element: Element): NetworkInstance => {
    let instance: NetworkInstance = new NetworkInstance();
    let item: Element;
    let attr: Attr;

    // isPriority - attribute is not parsed

    item = element.querySelector("server");
    instance.ip = item ? item.textContent : "";

    item = element.querySelector("port");
    instance.port = item ? +item.textContent : 0;

    item = element.querySelector("zone");
    instance.zone = item ? item.textContent : "";

    item = element.querySelector("zoneId");
    instance.zoneId = item ? +item.textContent : 0;

    item = element.querySelector("instanceName");
    instance.instanceCountID = item ? +item.textContent : 0;

    item = element.querySelector("isInternational");
    instance.isInternational = item && item.textContent === "1";

    item = element.querySelector("isFavorite");
    instance.isFavorite = item && item.textContent === "1";

    item = element.querySelector("defaultcountry");
    instance.defaultcountry = item ? item.textContent : "";

    item = element.querySelector("instanceLocaId");
    instance.instanceLocaId = item ? item.textContent : "";

    attr = element.attributes.getNamedItem("value");
    instance.instanceId = attr ? +attr.textContent : 0;

    item = element.querySelector("countries");
    let countries: string[] = item ? parseInstanceCountries(item.textContent) : [];

    if (countries) {
        instance.countries = createCountries(countries);
    }

    return instance;
}

export const parseNetworkProperties = (data: any): NetworkProperties => {
    let parser = new DOMParser();
    let doc: Document = parser.parseFromString(data, "text/xml");
    let properties: NetworkProperties = new NetworkProperties();
    let attr: Attr;

    attr = data.attributes.getNamedItem("deployTime");
    properties.deployTime = attr ? +attr.textContent : 0;

    attr = data.attributes.getNamedItem("versionNo");
    properties.versionNumber = attr ? attr.textContent : "0";

    attr = data.attributes.getNamedItem("is_approval");
    properties.isApproval = attr ? attr.textContent === "true" : false;

    return properties;
}

export const parseNetworkSettings = (data: string): NetworkSettings => {
    const parser = new DOMParser();
    const doc: Document = parser.parseFromString(data, "text/xml");
    const general: Element = doc.querySelector("general");

    const settings: NetworkSettings = new NetworkSettings({
        allowedFullScreen: readBooleanChildTagValue(general, "allowedfullscreen"),
        defaultInstanceId: readNumberChildTagValue(general, "defaultinstance"),
        earnCredits: readNumberChildTagValue(general, "earncredits"),
        enableFeedMessages: readBooleanChildTagValue(general, "enablefeedmessages"),
        enableLonelyCow: readBooleanChildTagValue(general, "enablelonelycow"),
        inviteFriends: readBooleanChildTagValue(general, "invitefriends"),
        maxUsernameLength: readNumberChildTagValue(general, "maxusernamelength"),
        networkBuddies: readBooleanChildTagValue(general, "networkbuddies"),
        networkName: readStringChildTagValue(general, "networkname"),
        requestPayByJS: readBooleanChildTagValue(general, "requestpaybyjs"),
        showVersion: readBooleanChildTagValue(general, "showversion"),
        useExternalLinks: readBooleanChildTagValue(general, "useexternallinks"),
        useKeyBasedLogin: readBooleanChildTagValue(general, "usekeybaselogin"),
        usePayment: readBooleanChildTagValue(general, "usepayment")
    });

    return settings;
}

/**
 * Parses the countries node.
 *
 * The country node is expected to look like:
 * <countries>["DE"]</countries>
 *
 * @param countriesJSON The content of the <countries> node
 * @return A String list of ggsCountryCodes
 */
const parseInstanceCountries = (countriesJSON: string): string[] => {
    let instanceGGSCountryCodes: string[] = [];

    // Since JSON format is expected here the smallest String for only one country code will be: ["DE"]
    // Therefore the minimum length of the countriesJSON String is expected to be at least 6
    let validMinimumLength: number = 6;
    if (countriesJSON && countriesJSON !== "" && countriesJSON !== "null" && countriesJSON.length >= validMinimumLength) {
        instanceGGSCountryCodes = JSON.parse(countriesJSON);
    }

    return instanceGGSCountryCodes;
}

const createCountries = (countryCodes: string[]): Country[] => {
    let countries: Country[] = [];

    if (!countryCodes) {
        logger.debug("NetworkXMLParser.createCountries: ggsCountryCodes is empty");
    }

    for (let i = 0; i < countryCodes.length; i++) {
        const country: Country = Context.instance.get(CountriesManager).getCountryByCountryCode(countryCodes[i], true);
        if (country) {
            countries.push(country);
        }
    }

    return countries;
}

